import React from 'react';

import { styled } from '@compiled/react';

import { IconButton } from '@atlaskit/button/new';
import CrossIcon from '@atlaskit/icon/core/cross';
import { token } from '@atlaskit/tokens';

const ControlBarContainer = styled.div({
  position: 'fixed',
  bottom: token('space.300'),
  zIndex: 3,
  left: 0,
  width: '100%',
  maxWidth: '100%',
  display: 'flex',
  justifyContent: 'center',
  height: token('space.600'),
  pointerEvents: 'none',
  '& > *': {
    pointerEvents: 'auto',
  },
});

const ControlBarContent = styled.div({
  position: 'absolute',
  bottom: 0,
  background: token('color.background.neutral.bold.pressed'),
  boxShadow: token('elevation.shadow.overlay'),
  borderRadius: token('space.075'),
  padding: token('space.100'),
  color: token('color.text.inverse'),
  display: 'flex',
  alignItems: 'stretch',
  '& button': {
    color: `${token('color.text.inverse')}`,
    background: token('color.background.neutral.bold.pressed'),
  },
  '& button:hover': {
    color: `${token('color.text.inverse')}`,
    background: token('color.background.neutral.bold.hovered'),
  },
  '& button:active': {
    color: `${token('color.text.inverse')}`,
    background: token('color.background.neutral.bold'),
  },
  '& button[disabled]': {
    color: `${token('color.text.subtlest')}`,
  },
});

const LabelContainer = styled.div({
  display: 'flex',
  alignItems: 'center',
  gap: token('space.100'),
  paddingRight: token('space.200'),
});

const ActionsWrapper = styled.div({
  display: 'flex',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: token('space.100'),
  paddingLeft: token('space.200'),
  paddingRight: token('space.100'),
  position: 'relative',
  '&::before': {
    content: "''",
    top: '3px',
    position: 'absolute',
    bottom: '3px',
    width: '1px',
    left: 0,
    background: token('color.border.accent.gray'),
  },
});

type Props = {
  /**
   * Number of items currently selected. The bar renders nothing when this is
   * `0`, so consumers can render it unconditionally.
   */
  selectedCount: number;
  /**
   * Pre-formatted label that describes the current selection (e.g.
   * "3 tests selected"). Consumers own the wording / pluralization.
   */
  selectionLabel: React.ReactNode;
  /**
   * Called when the close icon is clicked.
   */
  onClearSelection: () => void;
  /**
   * Accessible label for the close icon button. Defaults to "Clear selection".
   */
  clearSelectionLabel?: string;
  /**
   * Optional bulk action buttons rendered to the right of the selection
   * label, separated by a vertical divider.
   */
  actions?: React.ReactNode;
  /**
   * Test id for the root container.
   */
  testId?: string;
};

export const BulkSelectControlBar = ({
  selectedCount,
  selectionLabel,
  onClearSelection,
  clearSelectionLabel = 'Clear selection',
  actions,
  testId,
}: Props) => {
  if (selectedCount === 0) {
    return null;
  }

  return (
    <ControlBarContainer data-testid={testId}>
      <ControlBarContent>
        <LabelContainer>
          <IconButton
            label={clearSelectionLabel}
            appearance="subtle"
            spacing="compact"
            icon={iconProps => (
              <CrossIcon {...iconProps} color={token('color.icon.inverse')} />
            )}
            onClick={onClearSelection}
          />
          {selectionLabel}
        </LabelContainer>
        {actions && <ActionsWrapper>{actions}</ActionsWrapper>}
      </ControlBarContent>
    </ControlBarContainer>
  );
};
